import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Theme } from '../styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from '@reach/router';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  loadMemorizerCards,
  selectMemorizeState,
  selectCurrentCardId,
  resetSession
} from './memorize.slice';
import { MemorizeCardList } from './MemorizeCardList';
import { MemorizeCardDisplay } from './MemorizeCardDisplay';
import { styleConstants } from '../styles/styleConstants';
import { uiActions } from '../ui/ui.slice';
import { SideMenu } from '../review/sideMenu/sideMenu';
export const Memorize = (_: RouteComponentProps) => {
  usePageTitle('Memorize · Memkeep');
  const dispatch = useDispatch();
  const { cards, isLoading, error } = useSelector(selectMemorizeState);
  const currentCardId = useSelector(selectCurrentCardId);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCardListOpen, setIsCardListOpen] = useState(false);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const closeCardList = useCallback(() => setIsCardListOpen(false), []);
  const toggleCardList = useCallback(() => setIsCardListOpen(prev => !prev), []);
  const openSideMenu = useCallback(() => dispatch(uiActions.openSideMenu()), [dispatch]);

  const openCardDetails = useCallback(() => {
    closeMenu();
    if (currentCardId !== null && currentCardId !== undefined && currentCardId !== '') {
      window.open(`/card/${encodeURIComponent(currentCardId)}`, '_blank', 'noopener,noreferrer');
    }
  }, [currentCardId, closeMenu]);

  useEffect(() => {
    dispatch(loadMemorizerCards());
    return () => {
      dispatch(resetSession());
    };
  }, [dispatch]);
  
  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingText>Loading memorizer cards...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }
  
  if (error !== null && error !== undefined && error !== '') {
    return (
      <Container>
        <ErrorContainer>
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorContainer>
      </Container>
    );
  }
  
  if (cards.length === 0) {
    return (
      <Container>
        <EmptyStateContainer>
          <EmptyStateMessage>
            No cards in Memorizer state.
            <br />
            <SubMessage>
              Move cards to Memorizer state from the Search page to start memorizing.
            </SubMessage>
          </EmptyStateMessage>
        </EmptyStateContainer>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <IconButton onClick={openSideMenu} aria-label="Open menu">
            <Icon viewBox="0 0 24 24">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </Icon>
          </IconButton>
          <IconButton onClick={toggleCardList} aria-label="Toggle card list">
            <Icon viewBox="0 0 24 24">
              <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
            </Icon>
          </IconButton>
          <Title>Memorize Cards</Title>
        </HeaderLeft>
        <HeaderRight>
          <Stats>{cards.length} cards in Memorizer state</Stats>
          {currentCardId !== null && currentCardId !== undefined && currentCardId !== '' && (
            <MenuAnchor>
              <MenuButton onClick={toggleMenu} aria-label="Card actions">⋮</MenuButton>
              {isMenuOpen && (
                <>
                  <MenuOverlay onClick={closeMenu} />
                  <MenuLayer>
                    <MenuItem onClick={openCardDetails}>Details</MenuItem>
                  </MenuLayer>
                </>
              )}
            </MenuAnchor>
          )}
        </HeaderRight>
      </Header>
      
      <Content>
        <RightPanel>
          <MemorizeCardDisplay />
        </RightPanel>

        {isCardListOpen && <Backdrop onClick={closeCardList} />}
        <Drawer $isOpen={isCardListOpen}>
          <MemorizeCardList cards={cards} />
        </Drawer>
      </Content>

      <SideMenu />
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: ${(props: { theme: Theme }) => props.theme.primaryBackground};
`;

const Header = styled.div`
  height: ${styleConstants.actionBarHeight};
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.hoverBackground};
  }
`;

const Icon = styled.svg`
  width: 22px;
  height: 22px;
  fill: currentColor;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Stats = styled.div`
  font-size: 14px;
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
`;

const MenuAnchor = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.hoverBackground};
  }
`;

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
`;

const MenuLayer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 140px;
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
  border: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 101;
  overflow: hidden;
`;

const MenuItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 13px;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
  transition: background-color 0.2s;

  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.hoverBackground};
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 90;
`;

const Drawer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 30%;
  min-width: 300px;
  max-width: 400px;
  z-index: 91;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
  transform: translateX(${(props: { $isOpen: boolean }) => (props.$isOpen ? '0' : '-100%')});
  transition: transform 0.25s ease;
`;

const RightPanel = styled.div`
  flex: 1;
  height: 100%;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const LoadingText = styled.div`
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const ErrorMessage = styled.div`
  color: ${(props: { theme: Theme }) => props.theme.dangerColor || '#dc3545'};
  font-size: 16px;
  text-align: center;
`;

const EmptyStateContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const EmptyStateMessage = styled.div`
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
  font-size: 18px;
  text-align: center;
  line-height: 1.6;
`;

const SubMessage = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
`;